import {
  ContentPasteGo as ContentPasteGoIcon,
  RotateLeft as RotateLeftIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

interface PreviousOperationPanelProps {
  expanded: boolean;
  onAccordionChange: (
    panel: string,
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  lastActiveQuote: any;
  loadingLastQuote: boolean;
  setFormData?: (data: any) => void;
  formData?: any;
  onEditing?: boolean;
}

const PreviousOperationPanel: React.FC<PreviousOperationPanelProps> = ({
  expanded,
  onAccordionChange,
  lastActiveQuote,
  loadingLastQuote,
  setFormData,
  formData,
  onEditing,
}) => {
  const handleCopyOperation = () => {
    if (lastActiveQuote && setFormData) {
      setFormData({
        ...formData,
        advance: lastActiveQuote.advance,
        rate: lastActiveQuote.rate,
        arrearRate: lastActiveQuote.arrearRate,
        operationCostComissionFixed: lastActiveQuote.operationCostFixed,
        operationCostComissionVariablePercentage:
          lastActiveQuote.operationCostVariablePercentage,
        expenses: lastActiveQuote.expenses.map(
          (expense: { name: string; amount: number; hasIva: boolean }) => ({
            name: expense.name,
            amount: expense.amount,
            hasIva: expense.hasIva,
          }),
        ),
      });
    }
  };

  return (
    <Accordion
      expanded={lastActiveQuote ? expanded : false}
      onChange={lastActiveQuote ? onAccordionChange('panel2') : undefined}
      disabled={!lastActiveQuote}
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          minHeight: 'auto',
          padding: '16px 20px',
          backgroundColor: 'white',
          '&.Mui-expanded': {
            minHeight: 'auto',
          },
        },
        '& .MuiAccordionSummary-content': {
          margin: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
        },
        '& .MuiAccordionDetails-root': {
          padding: '0 20px 20px',
          backgroundColor: 'white',
        },
        '&.Mui-disabled': {
          backgroundColor: 'grey.50',
        },
      }}
    >
      <AccordionSummary
        expandIcon={null}
        sx={{
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              backgroundColor: lastActiveQuote ? 'primary.main' : 'grey.400',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <RotateLeftIcon sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: lastActiveQuote ? 'text.primary' : 'text.disabled',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Solicitud Anterior
            </Typography>
            <Chip
              label={lastActiveQuote ? 'Detalles' : 'No disponible'}
              size="small"
              color={lastActiveQuote ? 'primary' : 'default'}
              variant="outlined"
              sx={{
                height: '18px',
                fontSize: '0.65rem',
                opacity: lastActiveQuote ? 1 : 0.6,
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={handleCopyOperation}
            disabled={!lastActiveQuote || !setFormData || !onEditing}
            sx={{
              color: 'primary.main',
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              '&:hover': {
                backgroundColor: 'grey.100',
                borderColor: 'grey.300',
              },
              '&:disabled': {
                color: 'grey.400',
                backgroundColor: 'grey.100',
                borderColor: 'grey.200',
              },
              borderRadius: '100px',
              padding: '4px 12px',
            }}
          >
            <ContentPasteGoIcon
              sx={{ fontSize: '1rem', paddingRight: '4px' }}
            />
            <Typography variant="caption" fontWeight={600}>
              Copiar
            </Typography>
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Box
          sx={{
            backgroundColor: 'grey.50',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            mb: 2,
          }}
        >
          {loadingLastQuote ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Cargando solicitud anterior...
            </Typography>
          ) : lastActiveQuote ? (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Solicitud
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {lastActiveQuote.requestId}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Anticipo (%)
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {lastActiveQuote.advance}%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Interés (%)
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {lastActiveQuote.rate}%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Interés mora (%)
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {lastActiveQuote.arrearRate}%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Comisión operación fijo
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(lastActiveQuote.operationCostFixed)} CLP
                  </Typography>
                </Stack>
              </Grid>
              {lastActiveQuote.operationCostVariablePercentage && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Comisión operación variable (%)
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {lastActiveQuote.operationCostVariablePercentage}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Otros gastos
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(
                      lastActiveQuote.expenses.reduce(
                        (
                          acc: number,
                          curr: {
                            name: string;
                            amount: number;
                            hasIva: boolean;
                          },
                        ) => acc + curr.amount,
                        0,
                      ),
                    )}{' '}
                    CLP
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ fontStyle: 'italic' }}
            >
              No hay operación anterior disponible para mostrar
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PreviousOperationPanel;
